// ═══ Hercules Cart Drawer ═══════════════════════════════════════
const $ = id => document.getElementById(id);
const qtyTimers = {};
const variantCache = {};
let cartAutoCloseTimer;
let refreshInFlight = false;
let refreshPending = false;
let toastTimer;
let gwpInFlight = false;
let gwpPending = false;
let cartCache = null;
let clearingOrphans = false;
let refreshPromise = null;

const _origFetch = window.fetch.bind(window);

function getCartEl() { return document.getElementById('cart'); }

// Variants currently configured as active gifts.
function getConfiguredGiftVariants() {
  const el = getCartEl();
  if (!el) return [];
  return [el.dataset.gwp1Variant, el.dataset.gwp2Variant, el.dataset.subGwpVariant]
    .filter(Boolean).map(String);
}

// Every variant that has EVER been a gift (current + retired). Retired IDs come from
// data-all-gwp-variants so gifts you've swapped out can still be swept from old carts.
function getAllGiftVariants() {
  const el = getCartEl();
  /* Liquid now appends the picker-selected ids to this list, so an empty or
     duplicated entry is normal - filter and dedupe rather than assuming the
     merchant curated it by hand. */
  const retired = (el?.dataset.allGwpVariants || '')
    .split(',').map(s => s.trim()).filter(Boolean);
  return Array.from(new Set([...getConfiguredGiftVariants(), ...retired]));
}

// LP tub-tier gifts: added by the lp-tub-tiers section, controlled by tub
// quantity (not the spend-threshold GWP slots). They carry `_lp_gift` so the
// threshold sweep below leaves them alone, but they must still be treated as
// free (excluded from paid totals) and clamped to qty 1.
function isLpGiftItem(item) {
  return !!(item && item.properties && item.properties._lp_gift);
}

// A line is a gift if it carries our hidden flag OR matches any known gift variant.
/* Re-check an _lp_gift line against the cart it now finds itself in.

   The buy box stamps the unlock rule onto the line when it adds the gift:
     _lp_gift_min    tubs required    (quantity gifts)
     _lp_gift_spend  cart cents required (spend gifts)
     _lp_gift_mode   'subscription'   (subscribe-only) | 'both' | 'onetime'
     _lp_gift_pid    the paid product the gift was earned against

   Gifts added BEFORE those properties existed - and any added by the
   lp-tub-tiers section, which does not stamp them - have no rule to check, so
   they qualify by default. That keeps this backwards compatible: the worst
   case is the old behaviour, never a gift removed for the wrong reason.

   Only NON-gift lines count toward the quantity, or two gifts would prop each
   other up. */
function lpGiftStillQualifies(item, cart) {
  const props = (item && item.properties) || {};

  const mode = props._lp_gift_mode || '';
  if (mode === 'subscription') {
    const stillSubscribed = cart.items.some(
      i => !isGiftItem(i) && i.selling_plan_allocation != null
    );
    if (!stillSubscribed) return false;
  }
  if (mode === 'onetime') {
    const allSubscribed = cart.items
      .filter(i => !isGiftItem(i))
      .every(i => i.selling_plan_allocation != null);
    if (allSubscribed) return false;
  }

  /* Spend gifts re-check against the WHOLE cart excluding gifts, not just the
     product they came from. The buy box could only measure the supply being
     chosen; by the time the cart runs this the shopper may have added other
     products, and a spend threshold should count all of it. */
  const spend = parseInt(props._lp_gift_spend, 10);
  if (spend > 0 && getNonGwpTotal(cart) < spend) return false;

  const min = parseInt(props._lp_gift_min, 10);
  if (min > 1) {
    const pid = String(props._lp_gift_pid || '');
    const qty = cart.items.reduce((total, i) => {
      if (isGiftItem(i)) return total;
      // No pid recorded: fall back to the whole cart rather than removing a
      // gift we cannot attribute.
      if (pid && String(i.product_id) !== pid) return total;
      return total + i.quantity;
    }, 0);
    if (qty < min) return false;
  }

  return true;
}

function isGiftItem(item) {
  if (item && item.properties && (item.properties._gwp || item.properties._gift)) return true;
  if (isLpGiftItem(item)) return true;
  return getAllGiftVariants().includes(String(item.variant_id));
}

/* A free product is not a gift. The guides are $0 downloads a shopper picks
   on purpose, nothing earned them, and they check out on their own (Jaydan,
   28 Sep: the guides are meant to be products people can check out with
   without buying anything). What tells one from a gift is nothing a shopper
   can fake from a URL: a gift is stamped or configured, and a free product
   needs no shipping. A $0 physical item that is neither is still held to the
   hard rule below, so a free towel cannot walk out on its own. */
function isFreeProduct(item) {
  return !!item && !isGiftItem(item) && (Number(item.final_line_price) || 0) === 0 && item.requires_shipping === false;
}
function hasFreeProduct(cart) {
  return !!(cart && cart.items && cart.items.some(isFreeProduct));
}
/* Nothing in the cart ships (a guide alone), so shipping is not a cost: the
   Shipping row and the free shipping rung say so. */
function nothingToShip(cart) {
  const c = cart || cartCache;
  return !!(c && c.items && c.items.length && c.requires_shipping === false);
}

// Kept for backwards-compatibility with existing callers.
function getGwpVariants() { return getAllGiftVariants(); }

function getNonGwpTotal(cart) {
  return cart.items.reduce((t, i) => isGiftItem(i) ? t : t + i.final_line_price, 0);
}

/* The market's money symbol (data-sym from cart-drawer.liquid: $ for AUD, NZD
   and USD, £ or € elsewhere), so a re-render prints what the first paint did. */
function moneySym() { const el = getCartEl(); return (el && el.dataset.sym) || '$'; }
function formatMoney(cents) { return moneySym() + (cents / 100).toFixed(2); }
function formatMoneyClean(cents) {
  const dollars = (Number(cents) || 0) / 100;
  return moneySym() + (Number.isInteger(dollars) ? dollars.toFixed(0) : dollars.toFixed(2));
}
/* What the Shipping row says. Under the threshold it states the standard
   rate when the drawer knows it (data-ship-rate: the Australian rate from
   the section settings) instead of "Calculated at checkout", so the cost is
   no surprise at checkout; at the threshold it says Free. */
function shipCostText(totalCents, shipThreshold, cart) {
  if (shipFree(totalCents, shipThreshold, cart)) return 'Free';
  const rate = standardShipRate(cart);
  return rate > 0 ? formatMoney(rate) : 'Calculated at checkout';
}
/* Free shipping is earned at the threshold, or when nothing in the cart ships.
   A threshold of 0 is a market with no free shipping (data-free-ship-threshold
   from the market's metafield), and nothing earns it there. */
function shipFree(totalCents, shipThreshold, cart) {
  return (shipThreshold > 0 && totalCents >= shipThreshold) || nothingToShip(cart);
}
/* Outside Australia the rate is the 0 to 2 kg band and the row goes back to
   "Calculated at checkout" over that, as the Liquid first paint does. */
function standardShipRate(cart) {
  const el = getCartEl();
  const rate = parseInt(el?.dataset.shipRate || '0', 10) || 0;
  const c = cart || cartCache;
  if (rate > 0 && el?.dataset.market && el.dataset.market !== 'au' && c && (c.total_weight || 0) > 2000) return 0;
  return rate;
}
function shipThresholdCents() {
  const el = getCartEl();
  const v = el ? parseFloat(el.dataset.freeShipThreshold) : NaN;
  return (Number.isFinite(v) ? v : 130) * 100;
}
/* The shipping the Total row and the footer add to the goods: the standard
   rate under the threshold, nothing at it or when nothing ships. The same
   sum as est_total_cents in cart-drawer.liquid, so the first paint and every
   re-render agree. */
function estShipCents(totalCents, shipThreshold, cart) {
  const c = cart || cartCache;
  if (!c || !c.item_count) return 0;
  if (shipFree(totalCents, shipThreshold, c)) return 0;
  return standardShipRate(c);
}
/* Every place the total is written: the summary's Total row and the pinned
   footer. One list, so the two can never show different numbers. */
function totalEls() {
  return [$('sum-total'), $('total') || $('foot-now')].filter(Boolean);
}
function escapeHTML(value) {
  return String(value ?? '').replace(/[&<>'"]/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[char]));
}
function cleanRewardLabel(label) {
  return escapeHTML(String(label || '').replace(/\s+at\s+\$?\d+(?:\.\d+)?\s*$/i, '').trim());
}
function rewardIconHTML(key) {
  /* the subscription reward line's two marks; the bars carry no icons */
  const icons = {
    subscription: '<svg viewBox="0 0 24 24" focusable="false"><path d="M17 2l4 4-4 4"></path><path d="M3 11V9a4 4 0 0 1 4-4h14"></path><path d="M7 22l-4-4 4-4"></path><path d="M21 13v2a4 4 0 0 1-4 4H3"></path></svg>',
    check: '<svg viewBox="0 0 24 24" focusable="false"><path d="M20 6 9 17l-5-5"></path></svg>'
  };
  return icons[key] || icons.check;
}


/* /cart: the drawer is the page (Jaydan, 26 Sep: one design and one set of
   colours, so the page and the drawer cannot be confused). The drawer's own
   element moves into the page's slot and loses its off-canvas frame; every
   id and handler stays, so nothing here needs a second copy. While docked,
   open and close do nothing but refresh, and the overlay never shows. */
const docked = (function () {
  const slot = document.querySelector('[data-cart-page-slot]');
  const cart = document.getElementById('cart');
  if (!slot || !cart) return false;
  slot.appendChild(cart);
  cart.classList.add('cart--page');
  document.body.classList.add('cart-docked');
  return true;
})();

/* ------------------------------------------------------------------
   Keep the drawer scrolling under a thumb.
   Two things were wrong on iPhones (Jaydan, 28 Sep). overflow:hidden on
   the body is enough for a mouse wheel, but iOS ignores it for touch, so
   a swipe past the end of the drawer's list could reach the page behind
   the overlay. And overscroll-behavior: contain on the list, which stops
   that on other browsers, trips a WebKit fault: once the list has been
   dragged past its end the scroller stops answering vertical swipes until
   one comes in at an angle, which read as the drawer freezing at the
   bottom. So the list no longer carries overscroll-behavior, and while
   the drawer is open every touch is checked instead: a vertical swipe
   that would carry the list past its top or bottom, or any swipe that did
   not start inside the list, is stopped before the page can take it.
   Horizontal swipes (the product row) are left to the browser.
   ------------------------------------------------------------------ */
// dev/sim/carttouch.mjs drives this with synthetic touches.
let _touchX = 0, _touchY = 0, _touchScroller = null, _touchGuardOn = false;
function _onTouchStart(e) {
  if (e.targetTouches.length !== 1) return;
  _touchX = e.targetTouches[0].clientX;
  _touchY = e.targetTouches[0].clientY;
  const t = e.target;
  _touchScroller = t && t.closest ? t.closest('#cart-items') : null;
  // A list sitting exactly on its top or bottom edge when a swipe begins is
  // what WebKit latches past, to the page. One pixel off the edge and the
  // list is the scroller the gesture belongs to.
  const s = _touchScroller;
  if (s && s.scrollHeight > s.clientHeight) {
    const max = s.scrollHeight - s.clientHeight;
    if (s.scrollTop <= 0) s.scrollTop = 1;
    else if (s.scrollTop >= max) s.scrollTop = max - 1;
  }
}
function _onTouchMove(e) {
  if (e.targetTouches.length !== 1) return;
  const cart = $('cart');
  if (!cart || !cart.classList.contains('open')) return;
  if (!_touchScroller) { e.preventDefault(); return; }
  const dx = e.targetTouches[0].clientX - _touchX;
  const dy = e.targetTouches[0].clientY - _touchY;
  if (Math.abs(dx) > Math.abs(dy)) return;
  const s = _touchScroller;
  const atTop = s.scrollTop <= 0;
  const atBottom = s.scrollTop + s.clientHeight >= s.scrollHeight - 1;
  if ((atTop && dy > 0) || (atBottom && dy < 0)) e.preventDefault();
}
let _pageY = 0, _pagePinned = false;
const _coarse = () => (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) || navigator.maxTouchPoints > 0;
// On a touch screen the page itself is pinned (position: fixed, offset to where
// it was), so there is no scrollable page for a gesture to fall through to.
// A mouse does not need it, and pinning would shift a sticky header behind a
// narrow desktop drawer.
function lockPage() {
  if (_pagePinned || !_coarse()) return;
  _pagePinned = true;
  _pageY = window.scrollY || window.pageYOffset || 0;
  document.body.style.top = `-${_pageY}px`;
  document.body.classList.add('cart-lock');
}
function unlockPage() {
  if (!_pagePinned) return;
  _pagePinned = false;
  document.body.classList.remove('cart-lock');
  document.body.style.top = '';
  window.scrollTo(0, _pageY);
}
function lockTouch() {
  if (_touchGuardOn) return;
  _touchGuardOn = true;
  document.addEventListener('touchstart', _onTouchStart, { passive: true });
  document.addEventListener('touchmove', _onTouchMove, { passive: false });
  lockPage();
}
function unlockTouch() {
  if (!_touchGuardOn) return;
  _touchGuardOn = false;
  document.removeEventListener('touchstart', _onTouchStart);
  document.removeEventListener('touchmove', _onTouchMove);
  unlockPage();
}

function openCart(options = {}) {
  if (docked) { if (options.refresh !== false) refreshCart(); return; }
  const shouldRefresh = options.refresh !== false;
  const shouldRenderCache = options.renderCache !== false;

  // Render the last known cart before the drawer slides in, so progress bars
  // are already visually correct when the customer sees the drawer.
  if (shouldRenderCache && cartCache) applyCart(cartCache);

  const cartEl = $('cart');
  const overlayEl = $('overlay');
  if (cartEl) cartEl.classList.add('open');
  if (overlayEl) overlayEl.classList.add('open');
  document.body.style.overflow = 'hidden';
  lockTouch();

  if (shouldRefresh) refreshCart();
}

function closeCart() {
  closeExtrasSheet();
  if (docked) return;
  $('cart').classList.remove('open');
  $('overlay').classList.remove('open');
  document.body.style.overflow = '';
  unlockTouch();
}

function resetAutoClose() {}

function refreshCart() {
  if (refreshInFlight) { refreshPending = true; return refreshPromise || Promise.resolve(cartCache); }
  refreshInFlight = true;
  refreshPromise = _origFetch('/cart.js')
    .then(r => { if (r.status === 429) throw new Error('Rate limited'); return r.json(); })
    .then(cart => {
      cartCache = cart;
      applyCart(cart);
      return cart;
    })
    .catch(err => {
      console.warn('[cart] refresh failed:', err.message);
      return cartCache;
    })
    .finally(() => {
      refreshInFlight = false;
      refreshPromise = null;
      if (refreshPending) { refreshPending = false; setTimeout(refreshCart, 300); }
    });
  return refreshPromise;
}

function refreshCartFresh() {
  if (!refreshInFlight) return refreshCart();
  return (refreshPromise || Promise.resolve()).then(() => refreshCart());
}

function applyCart(cart) {
  if (!cart) return;
  cartCache = cart;

  /* Gifts cannot outlive the thing that earned them.

     handleGWP already removes unqualified gifts, but only for lines it can
     match to a configured tier or an _lp_gift marker - a gift added by an app
     or an older code path matches neither, so it sat in an otherwise empty
     cart looking like a free order. Checking "is there anything PAID left"
     catches all of them regardless of who added them or how they were
     tagged. */
  if (cart.items && cart.items.length && !cart.items.some(i => !isGiftItem(i))) {
    if (!clearingOrphans) {
      clearingOrphans = true;
      _origFetch('/cart/clear.js', { method: 'POST' })
        .then(r => r.json())
        .then(empty => { clearingOrphans = false; applyCart(empty); })
        .catch(() => { clearingOrphans = false; });
      return;
    }
  }
  updateBadge(cart.item_count);
  reRenderItems(cart);
  updateSubGwpLabel(cart);
  updateGiftBar(cart);
  handleGWP(cart);
  reconcilePlan(cart);
  reconcileCode(cart);
}

/* ── Discount-code reconciler ──
   Separate from reconcilePlan() on purpose. Buy Box V3's cross-sell uses a
   real Shopify discount CODE for a whole-order percentage - no selling plan
   involved at all, which is why it never sets _xs_base_plan and the plan
   reconciler correctly leaves it alone. Without this second copy, removing
   the add-on there would leave the discount code active on the session with
   nothing paying for it - the exact same leak reconcilePlan closes for the
   tier's selling plan, just a different mechanism to detect and undo. */
let reconcilingCode = false;
/* The standalone free-gift limiter that sat here has been removed. It
   duplicated what handleGWP already does, and two passes mutating the same
   lines race: one issues quantity:0 against a key the other has already
   re-indexed, so the second call silently no-ops. The dedupe now lives in
   handleGWP's _lp_gift branch, which is the one place that already owned
   gift-line decisions. */

function reconcileCode(cart) {
  if (reconcilingCode || !cart || !cart.items) return;

  const addonHandles = new Set();
  cart.items.forEach(i => {
    const h = i.properties && i.properties._xs_addon;
    if (h) addonHandles.add(h);
  });

  const stray = cart.items.find(i => {
    const p = i.properties || {};
    return p._xs_taken && p._xs_code && !addonHandles.has(p._xs_taken);
  });
  if (!stray) return;

  reconcilingCode = true;
  const cleanProps = {};
  Object.keys(stray.properties || {}).forEach(k => {
    if (k === '_xs_taken' || k === '_xs_code') return;
    cleanProps[k] = stray.properties[k];
  });

  // Empty discount clears whatever code is currently applied to the cart.
  _origFetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ discount: '' })
  }).then(() => _origFetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: stray.key, quantity: stray.quantity, properties: cleanProps })
  })).then(() => {
    reconcilingCode = false;
    refreshCartFresh();
  }).catch(() => { reconcilingCode = false; });
}

/* ── Plan reconciler ──
   Same contract as the tier-select block's own copy of this: a tier line
   added with the cross-sell ticked carries _xs_taken (which add-on it was
   paired with) and _xs_base_plan (the plan to fall back to if that add-on
   is ever gone). Two independent copies of this check exist on purpose -
   the tier block's own script only runs on pages where that block renders,
   so it cannot catch a removal that happens after the shopper has navigated
   elsewhere. This copy lives here instead, in the cart drawer, which loads
   on every page - so it is the one that actually guarantees the discount
   gets reverted no matter where the removal happens.

   Both copies are idempotent and check the SAME properties, so having both
   active is harmless - whichever one sees the cart first does the work, and
   clears the markers so the other finds nothing left to do. */
let reconcilingPlan = false;
function reconcilePlan(cart) {
  if (reconcilingPlan || !cart || !cart.items) return;

  const addonHandles = new Set();
  cart.items.forEach(i => {
    const h = i.properties && i.properties._xs_addon;
    if (h) addonHandles.add(h);
  });

  const stray = cart.items.find(i => {
    const p = i.properties || {};
    return p._xs_taken && p._xs_base_plan && !addonHandles.has(p._xs_taken);
  });
  if (!stray) return;

  reconcilingPlan = true;
  const cleanProps = {};
  Object.keys(stray.properties || {}).forEach(k => {
    if (k === '_xs_taken' || k === '_xs_base_plan') return;
    cleanProps[k] = stray.properties[k];
  });

  _origFetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: stray.key,
      quantity: stray.quantity,
      selling_plan: parseInt(stray.properties._xs_base_plan, 10),
      properties: cleanProps
    })
  }).then(() => {
    reconcilingPlan = false;
    // Refresh so the drawer shows the reverted price, and check again in
    // case a second tier on the page also needs reverting.
    refreshCartFresh();
  }).catch(() => { reconcilingPlan = false; });
}

function updateBadge(count) {
  document.querySelectorAll('.cart-badge, #hbadge, #cpill').forEach(el => el.textContent = count);
  document.querySelectorAll('cart-count').forEach(el => {
    el.textContent = count;
    el.setAttribute('data-count', count);
  });
}

function updateShippingBar(totalCents, cart) {
  const bar = $('ship-bar');
  if (!bar) return;
  const state = getProgressState(cart || cartCache);
  const rows = $('reward-rows');
  const sub = $('sub-gwp-lbl');
  if (rows) rows.innerHTML = state.rowsHtml;
  if (sub) {
    sub.hidden = state.subHidden;
    sub.className = `sub-reward ${state.subUnlocked ? 'unlocked' : 'locked'}`;
    sub.innerHTML = state.subLabelHtml;
  }
}

function updateFooter(cart) {
  const foot = $('foot');
  const scrollFoot = $('scroll-foot');
  if (foot) foot.style.display = cart.item_count === 0 ? 'none' : '';
  if (scrollFoot) scrollFoot.style.display = cart.item_count === 0 ? 'none' : '';
  if (!scrollFoot) return;

  const gwpVariants = getGwpVariants();
  const nonGwpItems = cart.items.filter(i => !gwpVariants.includes(String(i.variant_id)) && i.final_price > 0);
  const lineTotal = nonGwpItems.reduce((t, i) => t + i.final_line_price, 0);
  /* Savings = compare-at minus paid, NOT just Shopify's applied discounts.

     original_line_price vs final_line_price only captures discounts Shopify
     APPLIED - automatic discounts, codes, subscription plans. A bundle is
     different: it is priced low outright, so Shopify sees a $206 item sold at
     $206 and reports zero discount. The real saving is compare_at vs price,
     which is why the cart said $50 while the buy box said hundreds.

     /cart.js line items do NOT carry compare_at_price. It is on the Liquid
     variant object and on /products/<handle>.js, so compare-at is fetched per
     PRODUCT and cached for the life of the page - a handful of requests once,
     not per render.

     Savings render immediately from the applied discounts, then increase when
     the lookup lands. Never blocks the drawer opening. */
  const savingsFromDiscounts = (items) =>
    items.reduce((t, i) => t + Math.max(0, i.original_line_price - i.final_line_price), 0);

  const savingsWithCompareAt = (items) =>
    items.reduce((t, i) => {
      const applied = Math.max(0, i.original_line_price - i.final_line_price);
      const compareAt = COMPARE_AT_CACHE[String(i.variant_id)];
      const versusRrp = compareAt
        ? Math.max(0, compareAt * i.quantity - i.final_line_price)
        : 0;
      /* Larger of the two, never the sum: a bundle with a compare-at AND a
         quantity discount must not be counted twice. */
      return t + Math.max(applied, versusRrp);
    }, 0);

  const savings = savingsWithCompareAt(nonGwpItems);

  // Order-level discounts (e.g. the quiz code) live on the cart, not the line items.
  // cart.total_price is AFTER all discounts; the gap vs the line total is the code's reduction.
  const orderDiscount = Math.max(0, lineTotal - cart.total_price);
  const finalTotal = (typeof cart.total_price === 'number') ? cart.total_price : lineTotal;

  const sub = $('sub');
  // Subtotal shows the line total (after line/qty discounts); the Total rows show
  // the real payable amount plus the estimated shipping.
  if (sub) sub.textContent = formatMoney(lineTotal);
  const shipThreshold = shipThresholdCents();
  const estTotal = finalTotal + estShipCents(finalTotal, shipThreshold, cart);
  totalEls().forEach(t => { t.textContent = formatMoney(estTotal); });

  /* _origFetch, not window.fetch: the patched fetch triggers a cart refresh on
     every call, and refreshing from inside a render would loop. */
  loadCompareAtPrices(nonGwpItems, () => {
    const updated = savingsWithCompareAt(nonGwpItems);
    const row = $('sav-row'), amt = $('sav-amt');
    if (row && amt) {
      if (updated > 0) {
        amt.textContent = '−' + formatMoney(updated);
        row.style.display = '';
      } else {
        row.style.display = 'none';
      }
    }

    updateFootTotal(cart);

    /* Repaint each line's struck price in place rather than re-rendering the
       drawer. A full re-render would run applyCart -> handleGWP, and writing to
       the cart from inside a render is how you get a refresh loop. */
    nonGwpItems.forEach((i) => {
      const compareAt = COMPARE_AT_CACHE[String(i.variant_id)];
      if (!compareAt) return;
      const was = Math.max(i.original_line_price, compareAt * i.quantity);
      if (was <= i.final_line_price) return;
      document
        .querySelectorAll('[data-was-for="' + i.variant_id + '"]')
        .forEach((el) => { el.textContent = formatMoney(was); });
    });
  });

  const savRow = $('sav-row'), savAmt = $('sav-amt');
  if (savRow && savAmt) {
    if (savings > 0) {
      savAmt.textContent = '−' + formatMoney(savings);
      savRow.style.display = '';
    } else {
      savRow.style.display = 'none';
    }
  }

  // Discount-code line: show the order-level reduction (quiz code) so the drawer total matches checkout.
  var discRow = $('cart-code-row'), discAmt = $('cart-code-amt');
  if (discRow && discAmt) {
    if (orderDiscount > 0) {
      discAmt.textContent = '−' + formatMoney(orderDiscount);
      discRow.style.display = '';
    } else {
      discRow.style.display = 'none';
    }
  }

  // Shipping row: "Free" once the free-shipping threshold is met (uses same total + threshold as the progress bar)
  const shipCostEl = $('ship-cost');
  if (shipCostEl) {
    shipCostEl.textContent = shipCostText(finalTotal, shipThreshold, cart);
    shipCostEl.classList.toggle('ship-free', shipFree(finalTotal, shipThreshold, cart));
  }
}

function updateSubGwpLabel(cart) {
  const lbl = $('sub-gwp-lbl');
  if (!lbl) return;
  const state = getProgressState(cart || cartCache);
  lbl.hidden = state.subHidden;
  lbl.className = `sub-reward ${state.subUnlocked ? 'unlocked' : 'locked'}`;
  lbl.innerHTML = state.subLabelHtml;
}

/* The gift bar above Add to cart (Jaydan, 17 Oct 2026): the next gift the
   cart earns, the dollar gap to it and a fill, from the same rows the
   drawer's progress bars paint, so the buy box and the drawer never
   disagree. Free shipping is a reward, not a gift, so it is left out; once
   every gift is earned the top one stays, marked unlocked. With nothing in
   the cart the gap reads as the spend that earns the first gift. */
/* The bar above Add to cart (Jaydan, 17 Oct: "a bar that is linked to the
   gifts in the cart that sits above the Add to cart button"; "attached to
   the add to cart button so when they add more it goes up"; "when it
   unlocks it moves to the next thing as it needs to match the carts GWPs
   and the GWPs on the buy block").

   Its rungs are the buy box's own gift rows (.gift-row: the guide with
   every order, the towel at a spend, free shipping at the market's
   threshold, the mystery supplement at three tubs), in the order the
   section lists them, so the bar says what the rows under it say; a page
   without rows falls back to the drawer's rows. A rung is judged on the
   cart plus what the button would add: hx-pdp.js writes the buy box's
   line total, its quantity and the product on #hx-pdp (data-pending,
   data-pending-qty, data-pid) on every change and fires hx:tier. A spend
   rule counts the cart's paid lines plus the pending total, a quantity
   rule the cart's units of this product plus the pending units, and a
   rung with both needs both. The bar shows the first rung not yet
   unlocked and the gap to it, in dollars or in units; once every rung is
   unlocked it says so. After an add (hx:atc) the bar reads the cart alone
   until the selection changes again, so the units just added are never
   counted twice. */
let pendingLive = true;
function pendingState() {
  const root = document.getElementById('hx-pdp');
  if (!root || !pendingLive) return { cents: 0, qty: 0, pid: root ? root.dataset.pid : '' };
  return { cents: parseInt(root.dataset.pending || '0', 10) || 0, qty: parseInt(root.dataset.pendingQty || '0', 10) || 0, pid: root.dataset.pid || '' };
}
function buyBoxRungs() {
  const rows = [...document.querySelectorAll('#hx-pdp .gift-row')];
  if (!rows.length) return null;
  return rows.map((g) => {
    const label = (g.querySelector('span')?.textContent || g.dataset.giftName || 'Gift').trim();
    return { name: /^free\b/i.test(label) ? label : 'Free ' + (/^[A-Z][a-z]/.test(label) && !/^Hercules/.test(label) ? label.charAt(0).toLowerCase() + label.slice(1) : label), need: parseInt(g.dataset.need || '1', 10) || 1, spend: parseInt(g.dataset.spend || '0', 10) || 0, mode: g.dataset.mode || 'both' };
  });
}
function updateGiftBar(cart) {
  const bar = document.querySelector('[data-gift-bar]');
  if (!bar) return;
  const nameEl = bar.querySelector('[data-gb-name]');
  const gapEl = bar.querySelector('[data-gb-gap]');
  const fill = bar.querySelector('[data-gb-fill]');
  const track = bar.querySelector('[data-gb-track]');
  const paint = (name, gap, pct, done) => {
    if (nameEl) nameEl.textContent = name;
    if (gapEl) gapEl.textContent = gap;
    if (fill) fill.style.setProperty('--fill', Math.round(pct) + '%');
    if (track) track.setAttribute('aria-valuenow', String(Math.round(pct)));
    bar.classList.toggle('done', !!done);
    bar.hidden = false;
  };
  const pend = pendingState();
  const rungs = buyBoxRungs();
  const c = cart || cartCache;
  if (rungs) {
    const el = getCartEl();
    const paidCents = (c ? getNonGwpTotal(c) : parseInt(el?.dataset.initialTotal || '0', 10) || 0) + pend.cents;
    const units = (c?.items || []).filter(i => !isGiftItem(i) && String(i.product_id) === String(pend.pid)).reduce((t, i) => t + i.quantity, 0) + pend.qty;
    const hasSub = (c?.items || []).some(i => i.selling_plan_allocation != null);
    const unit = document.getElementById('hx-pdp')?.dataset.unit || 'tub';
    rungs.forEach((r) => {
      r.spendOk = r.spend <= 0 || paidCents >= r.spend;
      r.qtyOk = r.need <= 1 || units >= r.need;
      r.planOk = r.mode !== 'subscription' || hasSub;
      r.unlocked = r.spendOk && r.qtyOk && r.planOk;
      const pcts = [];
      if (r.spend > 0) pcts.push(paidCents / r.spend * 100);
      if (r.need > 1) pcts.push(units / r.need * 100);
      r.pct = clampProgress(pcts.length ? Math.min(...pcts) : (r.unlocked ? 100 : 0));
    });
    const next = rungs.find(r => !r.unlocked);
    if (!next) { paint('Every gift', 'Unlocked', 100, true); return; }
    let gap;
    if (!next.spendOk) gap = paidCents > 0 ? 'Add ' + formatMoneyClean(next.spend - paidCents) + ' more' : 'Spend ' + formatMoneyClean(next.spend);
    else if (!next.qtyOk) { const n = next.need - units; gap = 'Add ' + n + ' more ' + unit + (n === 1 ? '' : 's'); }
    else gap = 'With a subscription';
    paint(next.name, gap, next.pct, false);
    return;
  }
  const state = getProgressState(c, pend.cents);
  const rows = state.rows.filter(r => r.key !== 'shipping');
  if (!rows.length) { bar.hidden = true; return; }
  const next = rows.find(r => !r.unlocked) || rows[rows.length - 1];
  const gap = Math.max(0, next.threshold - state.totalCents);
  paint(next.name, next.unlocked ? 'Unlocked' : (state.totalCents > 0 ? 'Add ' + formatMoneyClean(gap) + ' more' : 'Spend ' + formatMoneyClean(next.threshold)), next.pct, next.unlocked);
}
/* variant_id -> compare_at_price, in cents. Populated from
   /products/<handle>.js because /cart.js does not expose compare_at_price on
   line items. One request per distinct product per page; results are kept for
   the life of the page, and a product already being fetched is not fetched
   again. */
const COMPARE_AT_CACHE = {};
const COMPARE_AT_PENDING = {};

function loadCompareAtPrices(items, onDone) {
  const handles = {};
  items.forEach((item) => {
    if (COMPARE_AT_CACHE[String(item.variant_id)] !== undefined) return;
    const handle = (item.url || '').split('?')[0].split('/products/')[1];
    if (handle && !COMPARE_AT_PENDING[handle]) handles[handle] = true;
  });

  const list = Object.keys(handles);
  if (!list.length) return;

  let outstanding = list.length;
  list.forEach((handle) => {
    COMPARE_AT_PENDING[handle] = true;
    _origFetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((product) => {
        (product.variants || []).forEach((variant) => {
          /* null is a real answer - "this variant has no compare-at" - and is
             cached so we never re-request it. */
          COMPARE_AT_CACHE[String(variant.id)] =
            typeof variant.compare_at_price === 'number' ? variant.compare_at_price : null;
        });
      })
      .catch(() => {})
      .then(() => {
        outstanding -= 1;
        if (outstanding === 0 && typeof onDone === 'function') onDone();
      });
  });
}

function handleGWP(cart) {
  const el = getCartEl();
  if (!el || !cart) return;

  // If a GWP mutation is already running, mark a re-run so we never drop a reconciliation.
  if (gwpInFlight) { gwpPending = true; return; }

  const paidTotal = getNonGwpTotal(cart);
  const paidCount = cart.items.filter(i => !isGiftItem(i)).length;
  const noPaidProduct = paidCount === 0 || paidTotal <= 0;   // HARD RULE source

  const subVariant = String(el.dataset.subGwpVariant || '');
  const hasSub = cart.items.some(
    i => i.selling_plan_allocation != null && String(i.variant_id) !== subVariant
  );

  // Active gift slots and their qualifying condition.
  const tiers = [
    { key: 'gwp1', variantId: String(el.dataset.gwp1Variant || ''),
      enabled: el.dataset.gwp1Enabled === 'true',
      qualify: () => paidTotal >= (parseFloat(el.dataset.gwp1Threshold) * 100 || Infinity) },
    { key: 'gwp2', variantId: String(el.dataset.gwp2Variant || ''),
      enabled: el.dataset.gwp2Enabled === 'true',
      qualify: () => paidTotal >= (parseFloat(el.dataset.gwp2Threshold) * 100 || Infinity) },
    { key: 'gwp3', variantId: String(el.dataset.gwp3Variant || ''),
      enabled: el.dataset.gwp3Enabled === 'true',
      qualify: () => paidTotal >= (parseFloat(el.dataset.gwp3Threshold) * 100 || Infinity) },
  ];
  if (el.dataset.subGwpEnabled === 'true' && subVariant) {
    tiers.push({ key: 'sub', variantId: subVariant, enabled: true, qualify: () => hasSub });
  }

  const removals = [];   // { id, quantity }
  const seen = {};       // ensures only ONE of each gift survives

  // 1. Walk every gift line already in the cart and decide if it stays.
  cart.items.forEach(item => {
    if (!isGiftItem(item)) return;

    // LP tub-tier gifts are governed by the lp-tub-tiers section, not the
    // spend-threshold slots. Don't sweep them for failing a threshold — but do
    // remove them if the cart has no paid product left, and clamp to qty 1 so
    // shoppers can't stack multiples of a free gift.
    if (isLpGiftItem(item)) {
      const lpVid = String(item.variant_id);
      if (noPaidProduct) {
        removals.push({ id: item.key, quantity: 0 });
      } else if (!lpGiftStillQualifies(item, cart)) {
        // Its unlock condition no longer holds - tubs reduced below the
        // threshold, or the subscription removed.
        removals.push({ id: item.key, quantity: 0 });
      } else if (seen[lpVid]) {
        /* A SECOND LINE of a gift already kept. This is the case that was
           missing: the clamp below only caught one line at quantity 2, never
           two separate lines each at quantity 1 - which is exactly what the
           buy block and this drawer's own GWP pass produce when both add the
           same gift. Two shakers, two lines, both "valid", both shipped.

           seen[] was only being populated for tier-configured gifts further
           down, so _lp_gift lines never participated in the dedupe at all. */
        removals.push({ id: item.key, quantity: 0 });
      } else {
        seen[lpVid] = true;
        if (item.quantity > 1) removals.push({ id: item.key, quantity: 1 });
      }
      return;
    }

    const vid = String(item.variant_id);
    const tier = tiers.find(t => t.enabled && t.variantId && t.variantId === vid);
    const justified = !noPaidProduct && !!tier && tier.qualify() && !seen[vid] && item.quantity >= 1;

    if (justified) {
      seen[vid] = true;
      if (item.quantity > 1) removals.push({ id: item.key, quantity: 1 }); // clamp to 1
    } else {
      removals.push({ id: item.key, quantity: 0 }); // orphan / disabled / unqualified / $0 cart
    }
  });

  // 2. Add newly-qualified gifts that aren't present yet (skip entirely if no paid product).
  const present = cart.items.filter(isGiftItem).map(i => String(i.variant_id));
  const additions = [];
  if (!noPaidProduct) {
    tiers.forEach(t => {
      if (t.enabled && t.variantId && t.qualify() && !present.includes(t.variantId)) {
        additions.push({ id: t.variantId, key: t.key });
      }
    });
  }

  if (!removals.length && !additions.length) return;

  gwpInFlight = true;

  // Fade out anything we're about to remove for a smooth UI.
  removals.forEach(r => { if (r.quantity === 0) {
    const node = cart.items.find(i => i.key === r.id);
    if (node) fadeOutItem(node.key);
  }});

  const post = (url, body) => _origFetch(url, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  }).then(r => r.json());

  /* ── One request for every removal, not one each ──
     This was N sequential /cart/change.js calls chained on line KEY. Two
     problems, and they compounded:

       Speed - four orphaned gifts meant four serial round trips before the
       drawer updated. At ~300ms each that is over a second of the shopper
       watching items they already deleted sit on screen.

       Correctness - /cart/change.js RE-INDEXES the cart on every call, so
       every key after the first was computed against a cart that no longer
       existed. Later removals silently hit the wrong line or no line at all.
       That is why gifts stayed behind: the first went, the rest missed.

     /cart/update.js takes the whole set at once, keyed by VARIANT id rather
     than line key, so there is nothing to go stale and it is a single
     request. Quantity 0 clears every line of that variant, which also
     collapses duplicate gift lines in the same pass. */
  /* ARRAY form, not the variant-keyed object form.

     The object form ({ variantId: qty }) sets the TOTAL for that variant
     across every line, which is wrong here in two ways I nearly shipped:

       Deduping two gift lines of the same variant sent { 111: 0 } and removed
       BOTH, so the gift vanished instead of being reduced to one.

       Worse - if a shopper had BOUGHT a shaker and also earned a free one,
       both lines share a variant. Removing the gift sent { 222: 0 } and
       deleted the paid line with it.

     The array form addresses lines by POSITION: one entry per line, in cart
     order. Still a single request, but line-precise, so it can express
     "remove line 2, keep line 1" - which the object form cannot. Positions
     are read from the cart we were handed and sent in one shot, so unlike
     chained change.js calls there is no re-indexing between them. */
  const qtys = cart.items.map(i => i.quantity);
  let changed = false;
  removals.forEach(r => {
    const idx = cart.items.findIndex(i => i.key === r.id);
    if (idx !== -1) { qtys[idx] = r.quantity; changed = true; }
  });

  let chain = Promise.resolve();
  if (changed) {
    chain = chain.then(() => post('/cart/update.js', { updates: qtys }));
  }
  /* Additions still go one at a time - /cart/add.js has no batch form. They
     are fired in parallel rather than chained, since adds do not invalidate
     each other's identifiers the way keyed changes do. */
  if (additions.length) {
    chain = chain.then(() => Promise.all(additions.map(a =>
      post('/cart/add.js', { id: a.id, quantity: 1, properties: { _gwp: a.key } })
    )));
  }

  chain
    .then(() => _origFetch('/cart.js').then(r => r.json()))
    .then(updated => {
      cartCache = updated;
      updateBadge(updated.item_count);
      updateShippingBar(getNonGwpTotal(updated), updated);
      updateFooter(updated);
      reRenderItems(updated);
      updateSubGwpLabel(updated);
    })
    .catch(err => console.warn('[cart] GWP sync failed:', err.message))
    .finally(() => {
      gwpInFlight = false;
      if (gwpPending) { gwpPending = false; setTimeout(() => handleGWP(cartCache), 150); }
    });
}


function clampProgress(value) {
  value = Number(value) || 0;
  return Math.max(0, Math.min(100, value));
}


function getProgressState(cart, extraCents) {
  const el = getCartEl();
  const totalCents = (cart ? getNonGwpTotal(cart) : parseInt(el?.dataset.initialTotal || '0', 10) || 0) + (extraCents || 0);
  const items = cart?.items || [];
  const shipThreshold = shipThresholdCents();
  const gwp1Threshold = parseFloat(el?.dataset.gwp1Threshold) * 100 || 15000;
  const gwp2Threshold = parseFloat(el?.dataset.gwp2Threshold) * 100 || 20000;
  const gwp3Threshold = parseFloat(el?.dataset.gwp3Threshold) * 100 || 25000;
  const subVariant = el?.dataset.subGwpVariant || '';
  const subEnabled = el?.dataset.subGwpEnabled === 'true';
  const inCart = variant => !!variant && items.some(i => String(i.variant_id) === String(variant));
  const hasSub = items.some(i => i.selling_plan_allocation != null && String(i.variant_id) !== String(subVariant));

  /* One bar per reward (Jaydan, 26 Sep): free shipping and each enabled
     gift, each with its own spend, fill and words, in order of spend so the
     nearest rung reads first (the towel at $130 sits under shipping at
     $150, 25 Oct 2026). The Liquid in cart-drawer.liquid paints the same rows in
     the same order first. Labels came through cleanRewardLabel, so they are
     already escaped. */
  const name = label => 'Free ' + String(cleanRewardLabel(label)).toLowerCase().replace(/^free\s+/, '').trim();
  const rows = [
    { key: 'shipping', name: 'Free shipping', threshold: shipThreshold, added: false, enabled: shipThreshold > 0 },
    { key: 'gwp1', name: name(el?.dataset.gwp1Label || 'Free Mystery Box'), threshold: gwp1Threshold, added: inCart(el?.dataset.gwp1Variant), enabled: el?.dataset.gwp1Enabled === 'true' },
    { key: 'gwp2', name: name(el?.dataset.gwp2Label || 'Free Duffel Bag'), threshold: gwp2Threshold, added: inCart(el?.dataset.gwp2Variant), enabled: el?.dataset.gwp2Enabled === 'true' },
    { key: 'gwp3', name: name(el?.dataset.gwp3Label || 'Free Gift'), threshold: gwp3Threshold, added: inCart(el?.dataset.gwp3Variant), enabled: el?.dataset.gwp3Enabled === 'true' }
  ].filter(r => r.enabled).sort((a, b) => a.threshold - b.threshold).map(r => ({ ...r, unlocked: totalCents >= r.threshold || (r.key === 'shipping' && nothingToShip()), pct: clampProgress(totalCents / Math.max(r.threshold, 1) * 100) }));

  const rowsHtml = rows.map(r => `
    <div class="reward-row ${r.added ? 'added' : r.unlocked ? 'unlocked' : 'locked'}" data-tier="${r.key}">
      <div class="reward-row-head"><span class="reward-row-name">${r.name}</span><span class="reward-row-status">${r.unlocked ? 'Unlocked' : `Add ${formatMoneyClean(r.threshold - totalCents)} more`}</span></div>
      <div class="reward-row-track" role="progressbar" aria-label="${r.name}" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(r.pct)}"><i class="reward-row-fill" style="--fill: ${r.pct}%"></i></div>
    </div>`).join('');

  return {
    rowsHtml,
    rows,
    totalCents,
    subHidden: !subEnabled || !subVariant,
    subUnlocked: hasSub,
    subLabelHtml: (!subEnabled || !subVariant) ? '' : `
      <span class="sub-reward-dot" aria-hidden="true">${hasSub ? rewardIconHTML('check') : rewardIconHTML('subscription')}</span>
      <span class="sub-reward-copy">
        <strong>${hasSub ? 'Free Welcome Kit unlocked' : 'First time subscribe for Free Welcome Kit'}</strong>
        <span></span>
      </span>`
  };
}

function shipBarHTML(cart = cartCache) {
  const state = getProgressState(cart);
  /* No band at all where the market has no free shipping and no gift to earn
     (the Liquid first paint hides it the same way). */
  const empty = state.rows.length === 0 && state.subHidden;
  return `
    <div class="ship-bar rewards-bar" id="ship-bar"${empty ? ' hidden' : ''}>
      <div class="reward-rows" id="reward-rows">${state.rowsHtml}</div>
      <div class="sub-reward ${state.subUnlocked ? 'unlocked' : 'locked'}" id="sub-gwp-lbl"${state.subHidden ? ' hidden' : ''}>${state.subLabelHtml}</div>
    </div>`;
}

function itemHTML(item, gwpVariants) {
  const isFree = item.final_price === 0 || isGiftItem(item);
  /* Struck price must agree with the Savings row. That row counts compare-at,
     so this has to as well - otherwise a bundle showed "was $647.67, now
     $589.38" ($58) beside a savings line of $103.57, because the strike was
     using original_line_price (pre-DISCOUNT) while savings used compare-at.

     Larger of the two, matching savingsWithCompareAt(): never the sum. */
  const compareAtLine = COMPARE_AT_CACHE[String(item.variant_id)];
  const wasLinePrice = Math.max(
    item.original_line_price,
    compareAtLine ? compareAtLine * item.quantity : 0
  );
  const hasDiscount = wasLinePrice > item.final_line_price;
  const isSub = item.selling_plan_allocation?.selling_plan != null;
  const variantLine = item.variant_title && item.variant_title !== 'Default Title'
    ? `<div class="ivar">${escapeHTML(item.variant_title)}</div>` : '';

  /* Line item properties - what is actually INSIDE a bundle.

     Simple Bundles records each component choice as properties[Component
     Label]. Those reach the order and the packing slip, but this drawer only
     ever rendered variant_title, so a bundle looked like a single anonymous
     line and nobody could see or check the components before ordering.

     Underscore-prefixed keys are skipped: Shopify treats them as hidden, and
     they are internal plumbing here - _bundle_selection (the joined string
     that DRIVES the components, already shown individually below), and the
     _lp_gift_* rules the gift reconciler stamps on. Showing those would be
     noise at best and confusing at worst.

     Values come from an app and from merchant-entered labels, so both key and
     value are escaped. */
  /* Shorten the component label to the product name.

     Simple Bundles writes the whole product title plus the option type, e.g.
       "100% Creatine Monohydrate - Memory & Performance Supplement - Size (Dropdown 1)"
     which is six lines of cart at body size. Everything after the first
     " - " or " – " is marketing copy the shopper does not need here - they
     already know what they bought. Splitting on the first dash leaves
     "100% Creatine Monohydrate", which is what they would call it.

     Both dash characters are handled: the titles mix hyphen and en dash. */
  const shortLabel = (key) =>
    String(key)
      .split(/\s+[-–—]\s+/)[0]
      .replace(/\s*\((?:dropdown|select|choose)[^)]*\)\s*$/i, '')
      .trim();

  const propLines = Object.keys(item.properties || {})
    .filter((key) => key.charAt(0) !== '_')
    .filter((key) => {
      const v = item.properties[key];
      return v !== null && v !== undefined && String(v).trim() !== '';
    })
    .map((key) =>
      `<div class="iprop"><span class="iprop-k">${escapeHTML(shortLabel(key))}</span>${escapeHTML(String(item.properties[key]))}</div>`
    )
    .join('');
  const propsBlock = propLines ? `<div class="iprops">${propLines}</div>` : '';
  /* Percentage from the SELLING PLAN's own before/after price, not the
     line's original_line_price / final_line_price - those two only diverge
     for cart-level codes and automatic discounts. A selling plan's own
     discount is invisible to that pair; Shopify reports it separately, on
     the allocation itself. compare_at_price is what the line would cost
     without the plan, price is what the plan actually charges. This is why
     the badge was showing no number at all - it was reading a field that a
     subscription discount never touches. */
  let subPct = 0;
  if (isSub) {
    const alloc = item.selling_plan_allocation;
    const was = alloc.compare_at_price;
    const now = alloc.price;
    if (was > 0 && now < was) subPct = Math.round((1 - now / was) * 100);
  }
  /* Cross-sell lines carry _xs_code and are discounted by a whole-order code,
     not a selling plan - so they have no Subscribe & Save badge to show. This
     puts the saving in that same slot, so the shopper sees why the price
     dropped rather than an unexplained lower number.

     The percentage comes from _xs_pct, stamped at add-to-cart time. Reading
     it off the line means a code changed later cannot retro-relabel an order
     that was placed under the old rate. */
  /* Read the REAL discount off the line, never the stamped _xs_pct.

     _xs_pct is what the buy block intended to happen. Whether it actually
     happened is a different question: a discount CODE applied via
     /discount/CODE is a session-level thing, and until Shopify marks it
     applicable the cart's line prices do not move. Badging "Save 10%" from
     the stamped value meant the badge appeared whether or not a single cent
     came off - a code that had expired, hit its usage cap, or failed its
     minimum would still show the shopper a saving they were not getting.

     That is the same failure as the FREESHIP / BUNDLE SAVING chips removed
     from this drawer earlier: a discount-shaped label with nothing behind it.

     line_level_discount_allocations is Shopify's own report of what was
     actually taken off this line. If it is empty, nothing was discounted, and
     no badge is shown - however much the buy block meant to. */
  let xsPct = 0;
  const allocs = item.line_level_discount_allocations || [];
  if (allocs.length && item.original_line_price > 0) {
    const off = allocs.reduce((sum, a) => sum + (a.amount || 0), 0);
    if (off > 0) xsPct = Math.round((off / item.original_line_price) * 100);
  }
  const xsBadge = (!isSub && xsPct > 0)
    ? `<span class="sub-tag sub-tag--xsell">Save ${xsPct}%</span>` : '';

  const subLine = isSub
    ? `<span class="sub-tag">Subscribe & Save${subPct > 0 ? ' ' + subPct + '%' : ''}</span><div class="ifreq">${item.selling_plan_allocation.selling_plan.name}</div>`
    : xsBadge;
  const qtyBlock = isFree ? '<div></div>' : `
    <div class="qty">
      <button type="button" onclick="chQty('${item.key}',-1)">−</button>
      <input id="q-${item.key}" class="qty-input" type="number" min="0" value="${item.quantity}" data-prev="${item.quantity}"
        oninput="setQty('${item.key}',this.value)" onfocus="this.select()"/>
      <button type="button" onclick="chQty('${item.key}',1)">+</button>
    </div>`;
  const priceBlock = `
    <div class="iprice">
      ${(isFree && item.final_price > 0) || (!isFree && hasDiscount) ? `<div class="was" data-was-for="${item.variant_id}">${formatMoney(wasLinePrice)}</div>` : ''}
      <div class="now ${isFree ? 'now-free' : ''}" id="p-${item.key}">${isGiftItem(item) ? 'Included' : isFree ? 'Free' : formatMoney(item.final_line_price)}</div>
    </div>`;
  const removeBtn = isFree ? '' : `
    <button class="rm" onclick="rmItem('${item.key}')" title="Remove" aria-label="Remove item">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="3 6 5 6 21 6"></polyline>
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
        <path d="M10 11v6M14 11v6"></path>
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
      </svg>
    </button>`;
  const thumbInner = item.image
    ? `<img src="${item.image}" alt="${item.product_title}">`
    : item.product_title.slice(0,4).toUpperCase();

  const productUrl = (item.url || '#').split('?')[0];
  const nameBlock = isFree
    ? `<div class="iname">${item.product_title}</div>`
    : `<a class="iname" href="${productUrl}" onclick="closeCart()">${item.product_title}</a>`;

  return `
    <div class="item" data-key="${item.key}">
      <div class="thumb">${thumbInner}</div>
      <div class="info">
        ${nameBlock}
        ${variantLine}
        ${propsBlock}
        ${subLine}
        <div class="ifoot">
          ${qtyBlock}
          ${priceBlock}
        </div>
      </div>
      ${removeBtn}
    </div>`;
}

function scrollFootHTML(cart) {
  const shipThreshold = shipThresholdCents();
  const nonGwpTotal = getNonGwpTotal(cart);
  const shipRow = `<span id="ship-cost"${shipFree(nonGwpTotal, shipThreshold, cart) ? ' class="ship-free"' : ''}>${shipCostText(nonGwpTotal, shipThreshold, cart)}</span>`;
  const estTotal = cart.total_price + estShipCents(nonGwpTotal, shipThreshold, cart);
  /* Order summary, Estimated shipping, Total: the same rows and the same sums
     as the first paint in cart-drawer.liquid. The Total is also in the pinned
     footer; totalEls() writes both from one number. */
  return `
    <div class="cart-scroll-foot" id="scroll-foot"${cart.item_count === 0 ? ' style="display:none"' : ''}>
      <div class="sum-section-label">Order summary</div>
      <div class="sum-row"><span>Subtotal</span><span id="sub">${formatMoney(cart.total_price)}</span></div>
      <div class="sum-row sav" id="sav-row" style="display:none"><span>Savings</span><span id="sav-amt"></span></div>
      <div class="sum-row sav" id="cart-code-row" style="display:none"><span>Discount code applied</span><span id="cart-code-amt"></span></div>
      <div class="sum-row"><span>Estimated shipping</span>${shipRow}</div>
      <div class="sum-row sum-total"><span>Total</span><span id="sum-total">${formatMoney(estTotal)}</span></div>
      <div id="upsell-block"></div>
    </div>`;
}

function reRenderItems(cart) {
  const container = $('cart-items');
  const foot = $('foot');
  if (!container) return;
  const gwpVariants = getGwpVariants();

  if (cart.item_count === 0) {
    _origFetch('/cart.js').then(r => r.json()).then(fresh => {
      if (fresh.item_count > 0) return reRenderItems(fresh);
      container.innerHTML = shipBarHTML(fresh) + `
        <div class="empty" id="empty-state"><h3>Your cart is empty</h3></div>
        <div id="rec-block"></div>` + scrollFootHTML(fresh);
      if (foot) foot.style.display = 'none';
      updateShippingBar(0, fresh);
      renderRecs(fresh);
    }).catch(err => console.warn('[cart] verify failed:', err.message));
    return;
  }

  if (foot) foot.style.display = '';
  /* Gifts render as lines like everything else - picture, name, "Included"
     in the price slot, no stepper, no bin (that is what itemHTML does with a
     free line) - after the paid lines they were earned by, so the thing the
     shopper bought stays on top (Jaydan, 16 Sep). They used to sit as a
     names-only list under the summary. */
  const paidLines = cart.items.filter(i => !isGiftItem(i));
  const giftLines = cart.items.filter(i => isGiftItem(i));

  container.innerHTML = shipBarHTML(cart) +
    paidLines.concat(giftLines).map(i => itemHTML(i, gwpVariants)).join('') +
    `<div id="rec-block"></div>` + scrollFootHTML(cart);

  updateFootTotal(cart);
  updateShippingBar(getNonGwpTotal(cart), cart);
  updateFooter(cart);
  renderUpsell(cart);
}

function updatePriceUI(key, newQty, prevQty) {
  const pEl = $('p-' + key);
  if (!pEl || pEl.textContent === 'Included' || pEl.textContent === '$0.00' || prevQty <= 0) return;

  const currentLine = parseFloat(pEl.textContent.replace(moneySym(), ''));
  const unit = currentLine / prevQty;
  const newLine = unit * newQty;
  const delta = newLine - currentLine;
  pEl.textContent = moneySym() + newLine.toFixed(2);

  const subEl = $('sub');
  if (subEl) {
    const cur = parseFloat(subEl.textContent.replace(moneySym(), ''));
    subEl.textContent = moneySym() + Math.max(0, cur + delta).toFixed(2);
  }

  totalEls().forEach(totalEl => {
    const cur = parseFloat(totalEl.textContent.replace(moneySym(), ''));
    totalEl.textContent = moneySym() + Math.max(0, cur + delta).toFixed(2);
  });

  const qtyDiff = newQty - prevQty;
  document.querySelectorAll('.cart-badge, #hbadge, #cpill').forEach(el => {
    const cur = parseInt(el.textContent) || 0;
    el.textContent = Math.max(0, cur + qtyDiff);
  });
}

function chQty(key, delta) {
  const qEl = $('q-' + key);
  if (!qEl) return;
  const currentQty = parseInt(qEl.value) || 0;
  const newQty = Math.max(0, currentQty + delta);
  if (newQty === 0) { rmItem(key); return; }
  qEl.value = newQty;
  updatePriceUI(key, newQty, currentQty);
  debounceCartUpdate(key);
}

function setQty(key, value) {
  const newQty = parseInt(value);
  if (isNaN(newQty) || newQty < 1) return;
  const qEl = $('q-' + key);
  const prevQty = parseInt(qEl?.dataset.prev) || newQty;
  updatePriceUI(key, newQty, prevQty);
  if (qEl) qEl.dataset.prev = newQty;
  debounceCartUpdate(key);
}

function debounceCartUpdate(key) {
  clearTimeout(qtyTimers[key]);
  qtyTimers[key] = setTimeout(() => {
    const finalQty = parseInt($('q-' + key)?.value) || 0;
    if (finalQty === 0) { rmItem(key); return; }
    _origFetch('/cart/change.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: finalQty })
    }).then(r => r.json()).then(applyCart).catch(err => console.warn('[cart] qty update failed:', err.message));
  }, 400);
}

function fadeOutItem(key) {
  const el = document.querySelector(`[data-key="${key}"]`);
  if (el) { el.classList.add('removing'); setTimeout(() => el.remove(), 160); }
}

function rmItem(key) {
  fadeOutItem(key);
  _origFetch('/cart/change.js', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity: 0 })
  }).then(r => r.json()).then(applyCart).catch(err => console.warn('[cart] remove failed:', err.message));
  /* No toast on removal.

     fadeOutItem() above already confirms it - the line visibly disappears,
     which is clearer than a message. The toast appended to the PAGE rather
     than the drawer, so closing the cart left a bare "Removed" floating over
     whatever the shopper looked at next, with nothing to attach it to. */
}

function getSlots(prefix, count) {
  const el = getCartEl();
  if (!el) return [];
  const slots = [];
  for (let i = 1; i <= count; i++) {
    const variantId = el.dataset[`${prefix}${i}Variant`];
    if (variantId) slots.push({
      variantId, index: i,
      discountCode: el.dataset[`${prefix}${i}Discount`] || '',
      discountPct: parseFloat(el.dataset[`${prefix}${i}DiscountPct`]) || 0,
      /* Per-slot copy. Empty string means "not set" and falls back to the
         shared text, so a merchant only writes the fields that differ. */
      head: el.dataset[`${prefix}${i}Head`] || '',
      body: el.dataset[`${prefix}${i}Body`] || '',
      note: el.dataset[`${prefix}${i}Note`] || ''
    });
  }
  return slots;
}

function fetchVariant(variantId) {
  if (variantCache[variantId]) return Promise.resolve(variantCache[variantId]);
  try {
    const cached = sessionStorage.getItem(`hc_variant_${variantId}`);
    if (cached) {
      const v = JSON.parse(cached);
      variantCache[variantId] = v;
      return Promise.resolve(v);
    }
  } catch(e) {}

  return _origFetch(`/variants/${variantId}.js`).then(r => r.json()).then(v => {
    const needsProductFallback = !v.featured_image && (v.product_id || v.featured_image?.product_id);
    if (needsProductFallback) {
      const pid = v.product_id || v.featured_image.product_id;
      return _origFetch(`/products/${pid}.js`).then(r => r.json()).then(p => {
        if (!v.featured_image && p.images?.[0]) v.featured_image = { src: p.images[0].src || p.images[0] };
        if (!v.product_title) v.product_title = p.title;
        return cacheVariant(variantId, v);
      });
    }
    return cacheVariant(variantId, v);
  });
}

function cacheVariant(variantId, v) {
  variantCache[variantId] = v;
  try { sessionStorage.setItem(`hc_variant_${variantId}`, JSON.stringify(v)); } catch(e) {}
  return v;
}

function renderProductCard(v, onclick, discountPct = 0) {
  const pct = parseFloat(discountPct) || 0;
  const discounted = pct > 0 ? Math.round(v.price * (1 - pct / 100)) : null;
  const imgUrl = v.featured_image?.src || v.featured_image?.url || null;
  const imgInner = imgUrl
    ? `<img src="${imgUrl}" alt="${v.product_title || v.name}" loading="lazy" decoding="async">`
    : `<div class="pc-img-fallback">${(v.product_title || v.name).slice(0,4).toUpperCase()}</div>`;
  const priceHTML = discounted
    ? `<div class="pc-save">${pct}% off</div><div class="pc-pricing"><span class="pc-was">${formatMoney(v.price)}</span><span class="pc-price">${formatMoney(discounted)}</span></div>`
    : `<div class="pc-price">${formatMoney(v.price)}</div>`;
  return `
    <div class="pc">
      <div class="pc-img">${imgInner}</div>
      <div class="pc-name">${v.product_title || v.name}</div>
      ${priceHTML}
      <button class="pc-btn" type="button" onclick="${onclick}">Add to Cart</button>
    </div>`;
}

function renderRecs(cart) {
  const block = $('rec-block');
  if (!block || cart.item_count > 0) { if (block) block.innerHTML = ''; return; }
  const el = getCartEl();
  if (!el) { block.innerHTML = ''; return; }
  const cols = [1, 2, 3, 4].map(i => ({
    label: el.dataset[`col${i}Label`],
    url: el.dataset[`col${i}Url`],
    img: el.dataset[`col${i}Img`]
  })).filter(c => c.label && c.url);
  if (!cols.length) { block.innerHTML = ''; return; }
  block.innerHTML = `
    <div class="col-grid">
      ${cols.map(c => `
        <a class="col-card" href="${c.url}" onclick="closeCart()">
          <div class="col-tile-wrap">${c.img ? `<img class="col-img" src="${c.img}" alt="${c.label}" loading="lazy">` : ''}</div>
          <div class="col-label">${c.label}</div>
        </a>`).join('')}
    </div>`;
}

/* Footer total + compare-at, and the discount chips.

   The saving here is the SAME calculation the line items use - larger of the
   applied discount and the compare-at gap - so the footer can never contradict
   the Savings row above it. */
function updateFootTotal(cart) {
  const now = document.getElementById('foot-now');
  const was = document.getElementById('foot-was');
  /* foot-off removed from the markup - the struck price says it. Kept as a
     null-safe lookup so an older cart-drawer.liquid does not break. */
  const off = document.getElementById('foot-off');
  if (!now) return;

  const paid = cart.items.filter(i => !isGiftItem(i));

  const total = cart.total_price;
  const wasTotal = paid.reduce((t, i) => {
    const compareAt = COMPARE_AT_CACHE[String(i.variant_id)];
    const versusRrp = compareAt ? compareAt * i.quantity : 0;
    return t + Math.max(i.original_line_price, versusRrp);
  }, 0);

  /* The footer carries the goods plus the estimated shipping, the same number
     as the summary's Total row; the struck price gets the same shipping so
     the gap between them is still the saving on the goods. */
  const estShip = estShipCents(total, shipThresholdCents(), cart);
  totalEls().forEach(t => { t.textContent = formatMoney(total + estShip); });

  const saving = wasTotal - total;
  const show = saving > 0 && wasTotal > 0;
  if (was) {
    was.hidden = !show;
    if (show) was.textContent = formatMoney(wasTotal + estShip);
  }
  if (off) {
    off.hidden = !show;
    if (show) off.textContent = Math.round((saving / wasTotal) * 100) + '% Off';
  }
}

/* updateSumChips() removed.

   It rendered FREESHIP / BUNDLE SAVING / SAVING W/ SUBSCRIPTION / FREE + GIFT
   as code-shaped pills in the summary. The conditions behind them were true,
   but the FORM was a lie: a pill that looks like a coupon code sitting above a
   total tells the shopper a code is in play. When checkout then shows the same
   total with no further reduction, the reasonable conclusion is that something
   did not apply - and the reasonable response is to leave and go looking for
   the code.

   Real reductions are still stated, as money, in the rows above: Savings and
   Discount code applied. Both are computed from the cart, so both survive
   contact with checkout.

   Worth a compliance read before anything of this shape goes back in - a
   displayed discount indicator that does not correspond to an actual reduction
   is the kind of thing the ACCC treats as a misleading price representation,
   and the exposure is larger where a struck-through RRP sits next to it. */


/* ============================================================
   THE EXTRAS RAIL  (Jaydan, 8 Oct)

   After Gymshark's "Add a little extra" block: a rail of empty cards under
   the summary, the next card cut at the edge as the cue to scroll, one
   heading, and one line that is about the order rather than the products.
   What Gymshark cannot do and we can: the drawer already knows what each
   item would unlock, so each card says it ("Gets you the free towel").

   The products come from the section's Extra blocks, written as JSON in
   cart-drawer.liquid (#cart-extras-data) at render time, so nothing is
   fetched. A product already in the cart is left out; one with nothing in
   stock never reaches the JSON. The control is the words "Add +", not a
   pill. On a sized product it opens the size sheet (#extras-sheet) inside
   the drawer: the item, then one row per size in stock, each its own Add +.

   With no blocks, the four typed-variant slots of the old strip still
   work: their variants are fetched once and drawn as unsized cards.
   ============================================================ */

let _extrasData = null;
const _extrasVariants = {};
let _sheetTrigger = null;

function extrasData() {
  if (_extrasData) return _extrasData;
  const node = document.getElementById('cart-extras-data');
  if (!node) return (_extrasData = []);
  try {
    _extrasData = (JSON.parse(node.textContent) || []).filter(Boolean)
      .map(it => ({ ...it, sizes: (it.sizes || []).filter(Boolean) }));
  } catch (err) {
    console.warn('[cart] extras data unreadable:', err.message);
    _extrasData = [];
  }
  return _extrasData;
}

/* The old slots, as cards. Variants arrive once; until they do the rail
   simply has fewer cards, and onReady repaints when the last one lands. */
function legacyExtras(onReady) {
  const slots = getSlots('upsell', 4);
  const items = [];
  let pending = 0;
  slots.forEach(slot => {
    const v = _extrasVariants[slot.variantId];
    if (v) {
      if (v.available !== false) items.push({
        product_id: v.product_id, name: v.product_title || v.name || '', colour: '', price: v.price,
        image: v.featured_image?.src || v.featured_image?.url || '', sized: false, slot: slot.index,
        sizes: [{ id: v.id, label: v.title || '', price: v.price, available: v.available !== false }]
      });
      return;
    }
    pending++;
    fetchVariant(slot.variantId)
      .then(fetched => { _extrasVariants[slot.variantId] = fetched; })
      .catch(err => console.warn('[cart] extras fetch failed:', err.message))
      .then(() => { if (--pending === 0) onReady(); });
  });
  return items;
}

/* The rewards still open, from the same rows the bars are drawn from, each
   with the spend still needed. */
function openRewards(cart) {
  const state = getProgressState(cart);
  return state.rows.filter(r => !r.unlocked).map(r => ({ ...r, gap: r.threshold - state.totalCents }));
}

function joinAnd(parts) {
  if (parts.length <= 1) return parts.join('');
  return parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1];
}

/* "the free towel", "free shipping"; with shipping in the same list a gift
   drops its "free", so the line reads "the towel and free shipping". */
function rewardPhrase(r, withShipping) {
  if (r.key === 'shipping') return 'free shipping';
  const bare = String(r.name).toLowerCase().replace(/^free\s+/, '').trim();
  return withShipping ? `the ${bare}` : `the free ${bare}`;
}

function extrasBandLine(open) {
  if (!open.length) return 'Ships free with the rest of your order.';
  return `You're ${joinAnd(open.map(r => `${formatMoneyClean(r.gap)} from ${rewardPhrase(r, false)}`))}.`;
}

/* Each card's line, from the same gaps as the band: one clause, result
   first, and only when the item unlocks something. The price covers a
   reward: "Unlocks the free towel", "Unlocks free shipping", "Unlocks the
   towel and free shipping" when it covers both. It falls short of every
   open reward: no line (Jaydan, 10 Oct: "$5 more for the free towel" reads
   as if the item costs $5 more, so say the unlock or nothing). Nothing
   open: no line, the band has it. What comes after the unlock is not said
   either: the bars move the moment the item lands. */
function extrasCardLine(priceCents, open) {
  const got = open.filter(r => priceCents >= r.gap);
  if (!got.length) return '';
  const withShipping = got.some(r => r.key === 'shipping');
  return `Unlocks ${joinAnd(got.map(r => rewardPhrase(r, withShipping)))}`;
}

function extrasAddHTML(attrs) {
  return `<button class="ups-add" type="button" ${attrs}>Add <i aria-hidden="true">+</i></button>`;
}

function extrasCardHTML(it, open) {
  const line = extrasCardLine(it.price, open);
  const first = it.sizes.find(sz => sz.available !== false);
  const action = it.sized
    ? extrasAddHTML(`data-extra-sheet="${escapeHTML(String(it.product_id))}" aria-haspopup="dialog"`)
    : extrasAddHTML(`data-ups-add="${escapeHTML(String(first.id))}" data-ups-slot="${it.slot || 0}"`);
  return `
    <div class="ups-card" role="listitem" data-extra="${escapeHTML(String(it.product_id))}">
      <div class="ups-thumb">${it.image ? `<img src="${escapeHTML(it.image)}" alt="" loading="lazy" decoding="async" width="56" height="56">` : ''}</div>
      <div class="ups-info">
        <div class="ups-name">${escapeHTML(it.name)}</div>
        <div class="ups-price"><strong>${formatMoney(it.price)}</strong></div>
        ${line ? `<div class="ups-line">${escapeHTML(line)}</div>` : ''}
      </div>
      ${action}
    </div>`;
}

function renderUpsell(cart) {
  const block = $('upsell-block');
  if (!block) return;
  if (!cart || cart.item_count === 0) { block.innerHTML = ''; closeExtrasSheet(); return; }

  const el = getCartEl();
  const inCart = new Set((cart.items || []).map(i => String(i.product_id)));
  let items = extrasData();
  if (!items.length) items = legacyExtras(() => renderUpsell(cartCache || cart));
  items = items.filter(it => it && it.sizes.some(sz => sz.available !== false) && !inCart.has(String(it.product_id)));
  if (!items.length) { block.innerHTML = ''; return; }

  const open = openRewards(cart);
  const order = el?.dataset.extrasOrder || 'auto';
  if (order === 'auto' && open.length) items = items.slice().sort((a, b) => a.price - b.price);

  const heading = el?.dataset.upsellHeading || "While you're here";
  block.innerHTML = `
    <div class="ups" id="extras">
      <div class="ups-head">${escapeHTML(heading)}</div>
      <div class="ups-body">${escapeHTML(extrasBandLine(open))}</div>
      <div class="ups-rail" role="list" aria-label="${escapeHTML(heading)}">${items.map(it => extrasCardHTML(it, open)).join('')}</div>
    </div>`;
}

/* The size sheet. Lives in #extras-sheet inside the drawer, over the
   scrim, so the shopper never leaves the cart. The row is the tap target
   and carries the variant, so the add path is the same as a card's. */
function openExtrasSheet(productId, trigger) {
  const wrap = $('extras-sheet');
  const it = extrasData().find(x => String(x.product_id) === String(productId));
  if (!wrap || !it) return;
  const open = openRewards(cartCache);
  const line = extrasCardLine(it.price, open);
  const sizes = it.sizes.filter(sz => sz.available !== false);
  const bigImage = it.image ? it.image.replace(/width=160/, 'width=240') : '';
  /* The scrim is the wrapper's own background, not an element of its own:
     theme.css hides every empty div (div:empty{display:none}), and an empty
     scrim div vanished on the live store while the sheet showed over an
     undimmed drawer (Jaydan, 8 Oct). A tap on the wrapper outside the sheet
     closes it, below. */
  wrap.innerHTML = `
    <div class="xs" role="dialog" aria-modal="true" aria-label="Add to cart" tabindex="-1">
      <div class="xs-head"><b>Add to cart</b><button class="xs-x" type="button" data-xs-close aria-label="Close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>
      <div class="xs-product">
        <div class="xs-img">${bigImage ? `<img src="${escapeHTML(bigImage)}" alt="" decoding="async" width="120" height="120">` : ''}</div>
        <div>
          <div class="xs-name">${escapeHTML(it.name)}</div>
          ${it.colour ? `<div class="xs-colour">${escapeHTML(it.colour)}</div>` : ''}
          <div class="xs-price">${formatMoney(it.price)}</div>
          ${line ? `<div class="ups-line">${escapeHTML(line)}</div>` : ''}
        </div>
      </div>
      <div class="xs-label">Select size</div>
      ${sizes.map(sz => `<button class="xs-row" type="button" data-ups-add="${escapeHTML(String(sz.id))}" data-ups-slot="0"><span>${escapeHTML(sz.label)}${sz.price !== it.price ? ` <small>${formatMoney(sz.price)}</small>` : ''}</span><span class="ups-add">Add <i aria-hidden="true">+</i></span></button>`).join('')}
    </div>`;
  wrap.hidden = false;
  _sheetTrigger = trigger || null;
  /* Focus the sheet itself, not its cross: the dialog is where the keyboard
     should land, and a ring around the cross on open reads as a box. */
  wrap.querySelector('.xs')?.focus({ preventScroll: true });
}

function closeExtrasSheet() {
  const wrap = $('extras-sheet');
  if (!wrap || wrap.hidden) return;
  wrap.hidden = true;
  wrap.innerHTML = '';
  if (_sheetTrigger && document.contains(_sheetTrigger)) _sheetTrigger.focus();
  _sheetTrigger = null;
}

/* Delegated, so it survives the rail being redrawn on every cart change.
   Escape closes the sheet before the drawer gets to hear it. */
document.addEventListener('keydown', (event) => {
  const wrap = $('extras-sheet');
  if (event.key === 'Escape' && wrap && !wrap.hidden) { event.stopPropagation(); closeExtrasSheet(); }
}, true);

document.addEventListener('click', (event) => {
  if (event.target.id === 'extras-sheet' || event.target.closest('[data-xs-close]')) { closeExtrasSheet(); return; }

  const sheetButton = event.target.closest('[data-extra-sheet]');
  if (sheetButton) { openExtrasSheet(sheetButton.getAttribute('data-extra-sheet'), sheetButton); return; }

  const button = event.target.closest('[data-ups-add]');
  if (!button || button.dataset.busy) return;
  button.dataset.busy = '1';
  const original = button.innerHTML;
  /* The busy state changes the words in place and hides the glyph by CSS;
     nothing is removed from the document while the tap is still being
     dispatched, so the drawer's outside-click listener still sees the tap
     as inside the cart. Rewriting textContent here detached the glyph. */
  const label = button.classList.contains('xs-row') ? button.querySelector('.ups-add') : button;
  const words = label ? Array.from(label.childNodes).find(n => n.nodeType === Node.TEXT_NODE) : null;
  if (words) words.nodeValue = 'Adding\u2026';
  const inSheet = !!button.closest('.xs');
  Promise.resolve(
    addProduct(button.getAttribute('data-ups-add'), 'upsell', Number(button.getAttribute('data-ups-slot')))
  ).catch(() => {}).then(() => {
    delete button.dataset.busy;
    if (document.contains(button)) button.innerHTML = original;
    if (inSheet) closeExtrasSheet();
  });
});

function addProduct(variantId, source, slotIndex) {
  const btn = event.target;
  const card = btn.closest('.pc');
  if (card) {
    card.style.opacity = '0.4';
    card.style.pointerEvents = 'none';
    btn.textContent = '✓ Added';
  }
  const el = getCartEl();
  const discountCode = (source === 'upsell' && el && slotIndex)
    ? el.dataset[`upsell${slotIndex}Discount`] || null : null;

  _origFetch('/cart/add.js', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity: 1 })
  })
  .then(r => r.json())
  .then(addedItem => {
    const pct = el && slotIndex ? parseFloat(el.dataset[`upsell${slotIndex}DiscountPct`]) || 0 : 0;
    const effectivePrice = pct > 0 ? Math.round(addedItem.price * (1 - pct / 100)) : addedItem.price;
    const sub = $('sub');
    if (sub) sub.textContent = formatMoney(parseFloat(sub.textContent.replace(moneySym(), '')) * 100 + effectivePrice);
    totalEls().forEach(t => { t.textContent = formatMoney(parseFloat(t.textContent.replace(moneySym(), '')) * 100 + effectivePrice); });

    if (discountCode) {
      return _origFetch('/cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: discountCode })
      }).then(r => r.json());
    }
    return _origFetch('/cart.js').then(r => r.json());
  })
  .then(applyCart)
  .then(() => hcToast('Added to cart'))
  .catch(err => {
    console.error('[cart] add failed:', err);
    if (card) { card.style.opacity = ''; card.style.pointerEvents = ''; btn.textContent = 'Add to Cart'; }
  });
}

function hcToast(msg) {
  let el = document.querySelector('.hc-toast');
  if (!el) { el = document.createElement('div'); el.className = 'hc-toast'; document.body.appendChild(el); }
  el.textContent = msg;
  el.classList.add('show');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('show'), 2200);
}

/* ── Hold checkout until the cart is settled ──
   Gift sweeps and plan reconciles run asynchronously after any cart change.
   A shopper who clicks Checkout mid-flight leaves with whatever the cart
   happened to contain at that instant - an orphaned gift that was about to be
   removed, or a tier still on a bumped plan whose add-on had just gone.

   The button waits rather than being disabled: disabling it mid-click reads
   as broken, and this normally resolves in a few hundred milliseconds. The
   3s ceiling means a wedged reconcile can never strand someone on the page.
   Uses the anchor's own href rather than a hardcoded path so a shop on a
   subpath or a localised checkout URL still works. */
/* Checkout goes straight to /checkout. It used to hold the button on
   "Updating cart…" for up to three seconds while a gift sweep was in flight;
   the sweep finishes on its own and a gift line is a $0 product, so nothing
   there is worth holding the shopper for (Jaydan, 26 Sep). */
window.fetch = function(...args) {
  const url = args[0];
  const isAdd = typeof url === 'string' && url.includes('/cart/add');
  /* Removing the paired cross-sell line (creatine) happens via /cart/change
     or /cart/update, never /cart/add - watching add alone meant a removal
     never refreshed the cart here, so reconcilePlan() (added below) never
     got a chance to run and revert the tier's discount. */
  const isMutate = typeof url === 'string' && (url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear'));
  return _origFetch.apply(this, args).then(response => {
    if (isAdd && response.ok) {
      setTimeout(() => {
        refreshCartFresh().then(() => openCart({ refresh: false, renderCache: false }));
      }, 0);
    } else if (isMutate && response.ok) {
      /* Response body IS the updated cart for change/update - use it directly
         instead of firing another /cart.js round trip. That second request
         was pure latency: the data was already in hand.

         Removing it also closes a feedback loop I introduced with this
         branch. refreshCartFresh -> applyCart -> handleGWP, and handleGWP
         issues its own cart mutations. Those go through _origFetch so they
         skip this interceptor, but any OTHER surface's mutation would land
         here mid-flight and schedule a refresh against a cart that was
         already being rewritten. */
      response.clone().json().then(updated => {
        if (updated && updated.items) applyCart(updated);
      }).catch(() => { refreshCartFresh(); });
    }
    return response;
  });
};

const _origXhrOpen = XMLHttpRequest.prototype.open;
const _origXhrSend = XMLHttpRequest.prototype.send;
XMLHttpRequest.prototype.open = function(method, url, ...rest) {
  this._isCartAdd = typeof url === 'string' && url.includes('/cart/add');
  this._isCartMutate = typeof url === 'string' && (url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear'));
  return _origXhrOpen.call(this, method, url, ...rest);
};
XMLHttpRequest.prototype.send = function(...args) {
  if (this._isCartAdd) {
    this.addEventListener('load', () => {
      if (this.status >= 200 && this.status < 300) {
        setTimeout(() => {
          refreshCartFresh().then(() => openCart({ refresh: false, renderCache: false }));
        }, 0);
      }
    });
  } else if (this._isCartMutate) {
    this.addEventListener('load', () => {
      if (this.status >= 200 && this.status < 300) {
        setTimeout(() => { refreshCartFresh(); }, 0);
      }
    });
  }
  return _origXhrSend.apply(this, args);
};

document.addEventListener('DOMContentLoaded', () => {
  const overlay = document.getElementById('overlay');
  if (overlay && overlay.parentElement !== document.body) document.body.appendChild(overlay);

  // Hijack cart icon clicks
  document.addEventListener('click', e => {
    const cartBtn = e.target.closest('.cart-drawer-button');
    if (!cartBtn) return;
    e.preventDefault();
    e.stopPropagation();
    openCart();
  });

  // An empty cart has nothing to reconcile (no gifts, no plans, no codes) and
  // the badge and the drawer are server-rendered, so the load-time /cart.js
  // round trip and the render behind it are skipped; the first open or the
  // first add refreshes it (round 3, 11 Oct 2026). A missing attribute keeps
  // the refresh, and so does the cart page, where the drawer is the page.
  const initialCount = parseInt(getCartEl()?.dataset.initialItemCount, 10);
  if (docked || initialCount !== 0) refreshCart().catch(err => console.warn('[cart] init failed:', err.message));
  // The gift bar above Add to cart paints from the server's total at once;
  // the refresh above repaints it when a cart comes back.
  updateGiftBar(cartCache);
  document.addEventListener('hx:tier', () => { pendingLive = true; updateGiftBar(cartCache); });
  document.addEventListener('hx:atc', () => { pendingLive = false; });

  // Don't let a cart of only free gifts reach checkout. A free product (a
  // guide: $0, nothing to ship, not a gift) is a real order and goes through.
  document.addEventListener('click', e => {
    const chk = e.target.closest('#chk-btn, a.chk');
    if (!chk) return;
    const cart = cartCache;
    if (!cart) return;
    if (getNonGwpTotal(cart) <= 0 && !hasFreeProduct(cart)) {
      e.preventDefault();
      hcToast('Add a product to your cart before checking out');
      handleGWP(cart); // sweep any lingering gifts
    }
  }, true);

  // Outside-click closes drawer.
  //
  // The event's path, not contains(): a tap on the plus glyph of an Add +
  // was answered by rewriting the button's text, which detached the glyph
  // before this listener ran, so contains() read the tap as outside and the
  // drawer closed on the shopper who had just added something (Jaydan,
  // 8 Oct). composedPath() was fixed when the tap was dispatched, and a
  // target that has left the document can only have come from inside it.
  document.addEventListener('click', e => {
    const cart = document.getElementById('cart');
    if (!cart?.classList.contains('open')) return;
    const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
    if (path.includes(cart) || cart.contains(e.target) || !document.contains(e.target)) return;
    if (e.target.closest('.cart-drawer-button')) return;
    closeCart();
  });

  document.addEventListener('cart:updated', refreshCart);
  document.addEventListener('theme:cart:change', refreshCart);

  const cartCountEl = document.querySelector('cart-count');
  if (cartCountEl) {
    new MutationObserver(() => {
      const count = parseInt(cartCountEl.textContent);
      if (!isNaN(count)) document.querySelectorAll('#cpill').forEach(el => el.textContent = count);
    }).observe(cartCountEl, { childList: true, characterData: true, subtree: true });
  }
});